import React, { useEffect, useState } from "react";
import { api, formatError } from "../lib/api";
import { toast } from "sonner";
import { FolderOpen, Plus, Loader2 } from "lucide-react";

// Reusable dropdown that lets the user pick an existing project folder OR
// create a new one on the fly. `value` is the folder id (or "" for root).
// `onChange(id)` receives the selected folder id — "" means "no folder".
export default function ProjectFolderPicker({
  value = "",
  onChange,
  testidPrefix = "folder-picker",
}) {
  const [folders, setFolders] = useState([]);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");

  const load = () =>
    api
      .get("/projects")
      .then((r) => setFolders((r.data || []).filter((p) => p.is_folder)))
      .catch(() => setFolders([]));

  useEffect(() => { load(); }, []);

  const handleSelectChange = (e) => {
    const v = e.target.value;
    if (v === "__new__") {
      setCreating(true);
      return;
    }
    onChange?.(v);
  };

  const createFolder = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      const res = await api.post("/projects", { name, is_folder: true });
      toast.success("Folder created");
      await load();
      setNewName("");
      setCreating(false);
      onChange?.(res.data.id);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  if (creating) {
    return (
      <div className="grid gap-2" data-testid={`${testidPrefix}-new`}>
        <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
          New folder name
        </span>
        <div className="flex items-center gap-2">
          <input
            autoFocus
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="e.g. 2026 Launch Plan"
            data-testid={`${testidPrefix}-new-name`}
            className="flex-1 rounded-lg border border-shell-border px-3 py-2 text-sm focus:outline-none focus:border-primary/40"
            onKeyDown={(e) => {
              if (e.key === "Enter" && !busy) createFolder(e);
              if (e.key === "Escape") { setCreating(false); setNewName(""); }
            }}
          />
          <button
            type="button"
            onClick={createFolder}
            disabled={busy || !newName.trim()}
            data-testid={`${testidPrefix}-new-create`}
            className="btn-primary text-xs disabled:opacity-60 inline-flex items-center gap-1"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Create
          </button>
          <button
            type="button"
            onClick={() => { setCreating(false); setNewName(""); }}
            className="text-xs text-muted-foreground hover:text-foreground"
            data-testid={`${testidPrefix}-new-cancel`}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="grid gap-1" data-testid={testidPrefix}>
      <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground inline-flex items-center gap-1">
        <FolderOpen className="w-3 h-3" /> Save into
      </span>
      <select
        value={value || ""}
        onChange={handleSelectChange}
        data-testid={`${testidPrefix}-select`}
        className="rounded-lg border border-shell-border px-3 py-2 text-sm focus:outline-none focus:border-primary/40 bg-white"
      >
        <option value="">No folder</option>
        {folders.map((f) => (
          <option key={f.id} value={f.id}>{f.name}</option>
        ))}
        <option value="__new__">+ Create new folder…</option>
      </select>
    </div>
  );
}
